export const WEEKDAY_OPTIONS = [
    { value: 'monday', label: 'Monday', shortLabel: 'Mon' },
    { value: 'tuesday', label: 'Tuesday', shortLabel: 'Tue' },
    { value: 'wednesday', label: 'Wednesday', shortLabel: 'Wed' },
    { value: 'thursday', label: 'Thursday', shortLabel: 'Thu' },
    { value: 'friday', label: 'Friday', shortLabel: 'Fri' },
    { value: 'saturday', label: 'Saturday', shortLabel: 'Sat' },
    { value: 'sunday', label: 'Sunday', shortLabel: 'Sun' },
];

export type TimePeriod = 'AM' | 'PM';

function normalizeMeetingDayValue(value: string): string | null {
    const normalized = value.trim().toLowerCase();
    if (!normalized) return null;

    return WEEKDAY_OPTIONS.find(option =>
        option.value === normalized ||
        option.label.toLowerCase() === normalized ||
        option.shortLabel.toLowerCase() === normalized ||
        option.shortLabel.toLowerCase().replace('.', '') === normalized.replace('.', '')
    )?.value ?? null;
}

export function normalizeMeetingDays(meetingDays: string[] | null | undefined): string[] {
    const normalized = (Array.isArray(meetingDays) ? meetingDays : [])
        .flatMap(day => day.split(/[,/]/))
        .map(normalizeMeetingDayValue)
        .filter((day): day is string => day != null);
    return orderedMeetingDays([...new Set(normalized)]);
}

export function meetingDaysLabel(meetingDays: string[] | null | undefined, labelKey: 'label' | 'shortLabel' = 'shortLabel'): string | null {
    const selected = normalizeMeetingDays(meetingDays)
        .map(day => WEEKDAY_OPTIONS.find(option => option.value === day)?.[labelKey])
        .filter(Boolean);
    return selected.length ? selected.join(' / ') : null;
}

export function orderedMeetingDays(meetingDays: string[]): string[] {
    return WEEKDAY_OPTIONS.map(option => option.value).filter(day => meetingDays.includes(day));
}

export function startTimeParts(startTime: string | null | undefined): { hour: string; minute: string; period: TimePeriod } {
    const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(startTime ?? '');
    if (!match) return { hour: '', minute: '00', period: 'AM' };

    const hour24 = Number(match[1]);
    const period: TimePeriod = hour24 >= 12 ? 'PM' : 'AM';
    const hour12 = hour24 % 12 || 12;
    return { hour: String(hour12).padStart(2, '0'), minute: match[2], period };
}

export function startTimeValue(hour: string, minute: string, period: TimePeriod): string | null {
    if (!hour) return null;
    const hour12 = Number(hour);
    const hour24 = period === 'PM' ? (hour12 % 12) + 12 : hour12 % 12;
    return `${String(hour24).padStart(2, '0')}:${minute}`;
}

export function startTimeLabel(startTime: string | null | undefined): string | null {
    const parts = startTimeParts(startTime);
    return parts.hour ? `${Number(parts.hour)}:${parts.minute} ${parts.period}` : null;
}
